import React from "react";
import { useDesktopsStore } from "@/dentlab/src/store/Desktops";
import { ORG_SETTINGS_KEY } from "@/dentlab/src/store/Desktops/types";
import type { PrintMode } from "@/dentlab/src/store/Desktops/types";
import { Monitor, Globe } from "lucide-react";

interface PrintModeToggleProps {
	locationId: number;
	organizationId: string;
}

export const PrintModeToggle: React.FC<PrintModeToggleProps> = ({
	locationId,
	organizationId,
}) => {
	const { getOrgPrintMode, upsertPrinterConfig } = useDesktopsStore(
		(state) => ({
			getOrgPrintMode: state.getOrgPrintMode,
			upsertPrinterConfig: state.upsertPrinterConfig,
		})
	);

	const currentMode = getOrgPrintMode(locationId);

	const handleChange = (mode: PrintMode) => {
		if (mode === currentMode) return;
		upsertPrinterConfig({
			organization_id: organizationId,
			location_id: locationId,
			config_key: ORG_SETTINGS_KEY,
			print_mode: mode,
		});
	};

	return (
		<div data-testid="print-mode-toggle">
			<h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
				Druckmodus
			</h2>
			<div className="flex gap-3">
				<button
					type="button"
					onClick={() => handleChange("desktop")}
					className={`flex-1 rounded-lg border-2 p-4 text-left transition-colors ${
						currentMode === "desktop"
							? "border-blue-500 bg-blue-50"
							: "border-gray-200 hover:border-gray-300"
					}`}
					data-testid="print-mode-desktop"
					aria-label="Desktop-Druck auswählen"
				>
					<div className="flex items-center gap-2 mb-1">
						<Monitor className="h-4 w-4" />
						<span className="text-sm font-medium">
							Desktop-Druck
						</span>
						{currentMode === "desktop" && (
							<span className="text-xs text-blue-600 font-medium">
								Aktiv
							</span>
						)}
					</div>
					<p className="text-xs text-gray-500">
						Dokumente werden direkt an den konfigurierten Drucker
						gesendet. dentLAB Druckservice erforderlich.
					</p>
				</button>
				<button
					type="button"
					onClick={() => handleChange("browser")}
					className={`flex-1 rounded-lg border-2 p-4 text-left transition-colors ${
						currentMode === "browser"
							? "border-blue-500 bg-blue-50"
							: "border-gray-200 hover:border-gray-300"
					}`}
					data-testid="print-mode-browser"
					aria-label="Browser-Druck auswählen"
				>
					<div className="flex items-center gap-2 mb-1">
						<Globe className="h-4 w-4" />
						<span className="text-sm font-medium">
							Browser-Druck
						</span>
						{currentMode === "browser" && (
							<span className="text-xs text-blue-600 font-medium">
								Aktiv
							</span>
						)}
					</div>
					<p className="text-xs text-gray-500">
						Dokumente werden in einem neuen Tab geöffnet. Kein
						Druckservice erforderlich.
					</p>
				</button>
			</div>
		</div>
	);
};
